import "./utils/polyfills";
import React from "react";
import { render } from "react-dom";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import { StateProvider, getAppState } from "./state";
import { config } from "./config/config";
import localConfig from "./config/localConfig";
import { autologin } from "./services/authenticationService";
import { i18n } from "./services/i18nService";
import { Normalize } from "styled-normalize";
import { history } from "./services/historyService";
import { executeDeepLinkAction } from "./services/deepLinkService";
import { validateGeolocation, getGeolocation } from "./services/geolocationService";
import { getSdk } from "./services/sdkService";
import { getGlobalButtonInfo } from "./errors/globalButtons";
import { getLogger } from "./utils/logger";
import * as Sentry from "@sentry/react";
import { Integrations } from "@sentry/tracing";
import packageJson from "../package.json";
import { isForceLogout } from "./utils/isForceLogout";
import { isUserAccountActive } from "./services/accountStatusService";
import { getPageRouteName, isFromContentDetailsPath } from "./utils/SentryStorageLogger";
import { isAnyAddonStatusActive } from "./utils/subscriptionUtils";
import { checkLanguage } from "./utils/checkLanguage";

const buildInfo = getAppState().buildInfo;

let sentryInitData = {
  dsn: "https://6f26f6d4e144444d911fa781bb6311b8@sentry.io/6087298",
  integrations: [new Integrations.BrowserTracing()],
  tracesSampleRate: 1.0,
  environment: window.location.href.includes("stg") ? "staging" : localConfig.environment,
  beforeSend(event) {
    if (event.exception && event.exception.values) {
      const exception = event.exception.values[0];
      const url = event.request.url;
      const currentPage = getPageRouteName(url);
      if (
        (currentPage !== "home" && currentPage !== "player" && !isFromContentDetailsPath(url)) ||
        exception.type === "NotSupportedError"
      ) {
        return null;
      }
    }
    return event;
  }
};
if (process.env.NODE_ENV === "production") {
  sentryInitData.release = packageJson.version;
}

Sentry.init(sentryInitData);

let Logger;
let sdk;
/**
 * Initialized the app by performing the background tasks to initialize the needed data and also by starting the app
 * render process. Depending on where the app load is happening (whether at app root or in another section of the app,
 * like the detail page) the app start will behave slightly differently. In the fist case, background tasks and
 * rendering will happen in parallel, while in the second, the app will ensure the required background tasks are
 * complete before continuing with the rendering.
 */
export async function startApp() {
  Logger = getLogger();
  const configuration = await config.request();
  let locale = checkLanguage(getLocaleFromLocalCache());
  sdk = await getSdk();
  await getDeviceAndUserData();
  const getDeviceName = await sdk.systemManager.device.getPlatform();
  if (getDeviceName === "webos") {
    if (localStorage.getItem("lgSdkVersion") === null || localStorage.getItem("lgSdkVersion") === "") {
      const getServerVersion = await sdk.systemManager.device.getServerVersion();
    }
  }
  // Clean player previous errors.
  sdk.playerManager.destroy();
  Logger.log({ locale }, "startApp");
  configuration.featureToggles.analytics && sdk.analyticsManager.initialize();
  const requests = [
    i18n.init(locale || configuration.sdk.defaultLocale), // [0]
    i18n.requestLanguageFile(configuration.sdk.defaultLocale), // [1]
    validateGeolocation() // [2]
  ];
  const results = await requests.map(async req => await req);
  await results[1];
  const isGeoLocOk = await results[2];
  (await results[0]) && getAndValidateGeolocation(isGeoLocOk) && (await setCountry());
  Object.keys(configuration.locales).forEach(locale => {
    if (locale !== configuration.sdk.defaultLocale) {
      i18n.requestLanguageFile(locale);
    }
  });
  if (isAppFreshStart()) {
    // Check if there is a deepLinking Data on the fresh app start
    const deepLinkingData =
      sdk.systemManager.application.getDeepLinkTitle && sdk.systemManager.application.getDeepLinkTitle();
    deepLinkingData && executeDeepLinkAction.call({ history }, deepLinkingData);
    if (!deepLinkingData) {
      initAppAndRenderInParallel();
    } else {
      initAppAndRenderSequentially();
    }
  } else {
    initAppAndRenderSequentially();
  }

  // Device capabilities.
  getAppState().supports4K = await sdk.systemManager.device.supports4K();
}
// TODO: Move the validation logic to an outer middleware, to avoid exposing
// allowed countries in client-side.
async function getAndValidateGeolocation(isValidReq) {
  const geolocationGlobalModalConfig = {
    dismissable: false,
    modalText: i18n.get("global.errors.geoblocked"),
    buttons: [
      {
        ...getGlobalButtonInfo("exit"),
        buttonHandler: sdk.systemManager.application.exit
      }
    ]
  };

  if (!isValidReq) {
    getAppState().globalModal = geolocationGlobalModalConfig;
  }
}

function getLocaleFromLocalCache() {
  if (localStorage) {
    return localStorage.getItem("locale");
  }
}

/**
 * Returns if the app is being started from the route path '/' or from a different location.
 * @returns {boolean} True is the app is loading at the route, false in other case.
 */
function isAppFreshStart() {
  return window.location.pathname === "/";
}

/**
 * Sends an autologin request and starts the app render process without waiting for the response.
 */
async function initAppAndRenderInParallel() {
  const appState = getAppState();
  try {
    const subscriptions = await sdk.billingManager.getSubscriptions({ country: appState.country, avoidCache: false });
    appState.subscriptions = subscriptions;
    const loggedIn = await autologin();
    await updateAppStateIfLoggedIn(loggedIn);
  } catch (e) {
    const forceLogoutStatus = await isForceLogout(e);
    if (forceLogoutStatus) {
      appState.forceLogout = true;
    }
  }
  renderApp();
}

async function getDeviceAndUserData() {
  const appState = getAppState();
  const sdk = await getSdk();
  appState.deviceId = await sdk.systemManager.device.getId();
  appState.deviceModel = await sdk.systemManager.device.getModel();
}

/**
 * Ensures that the autologin attempt has been done and completed before starting the app render process.
 */
async function initAppAndRenderSequentially() {
  const appState = getAppState();
  try {
    const loggedIn = await autologin();
    await updateAppStateIfLoggedIn(loggedIn);
  } catch (e) {
    const forceLogoutStatus = isForceLogout(e);
    if (forceLogoutStatus === true) {
      appState.forceLogout = true;
    }
  }
  console.log("...Start Render App...");
  renderApp();
}

function renderApp() {
  render(
    <StateProvider>
      <BrowserRouter>
        <React.Fragment>
          <Normalize />
          <App />
        </React.Fragment>
      </BrowserRouter>
    </StateProvider>,
    document.getElementById("root")
  );
}

async function updateAppStateIfLoggedIn(loggedIn) {
  const appState = getAppState();
  const sdk = await getSdk();
  try {
    if (loggedIn) {
      appState.userLogged = true;
      appState.userLocale = checkLanguage(loggedIn.settings.language || loggedIn.locale);
      appState.userAccountStatus = loggedIn.settings.accountStatus;
      appState.parentalLevel = loggedIn.settings.parentalControl;
      appState.addons = loggedIn.settings.addons || [];
      appState.isActiveUser = isUserAccountActive(loggedIn.settings.accountStatus);
      appState.userAddonStatus = isAnyAddonStatusActive(loggedIn?.settings?.addons);
      appState.userRelatedModules = null;
      appState.list5NewsModules = null;
      appState.currentProfileType = loggedIn.profileType;
      appState.isGuestProfile = false;

      await sdk.userManager.getDevices();
    }

    if (!appState?.subscriptions) {
      const subscriptions = await sdk.billingManager.getSubscriptions({ country: appState.country, avoidCache: false });
      appState.subscriptions = subscriptions;
    }
  } catch (e) {
    // Force logout listening
    const forceLogoutStatus = await isForceLogout(e);
    if (forceLogoutStatus) {
      appState.forceLogout = true;
    }
  }
}

async function setCountry() {
  const { country } = await getGeolocation();
  country && (getAppState().country = country.toLowerCase());
}

/*
 This is a build time code that gets and shows the current app and dependencies versions.
 FIXME: Consider refactoring to move to a separate dedicated file, or integrating in the state management if possible
 */
(function() {
  try {
    getAppState().buildInfo = require("./build-info.json");
  } catch (e) {
    console.warn("No build-info.json present");
  }
})();
startApp();
